import { supabase } from '../lib/supabase';

/**
 * Fetch the job_request a review is being written for, joined with
 * pro profile and category info needed by ReviewPage.jsx.
 *
 * NOTE: job_requests.service_id (uuid) does NOT link to services.id (integer)
 * — known data-integrity gap. We deliberately do not join to `services`.
 * job_requests.description is used as the displayed service text instead.
 */
export async function fetchJobForReview(jobRequestId) {
  const { data, error } = await supabase
    .from('job_requests')
    .select(`
      id,
      description,
      location,
      scheduled_time,
      estimated_price,
      status,
      pro_id,
      user_id,
      category_id,
      categories ( id, name, icon ),
      pro_profiles ( id, full_name, avatar_url, rating, total_jobs, is_verified, location )
    `)
    .eq('id', jobRequestId)
    .single();

  if (error) throw error;
  return data;
}

/**
 * Fetch an existing review for this job_request (if one was already submitted),
 * including any pro_reply, so the page can render the "already reviewed" state.
 */
export async function fetchExistingReview(jobRequestId) {
  const { data, error } = await supabase
    .from('reviews')
    .select('*')
    .eq('job_request_id', jobRequestId)
    .maybeSingle();

  if (error) throw error;
  return data;
}

/**
 * Fetch the current authenticated user's profile (name + avatar) for the
 * "public review preview" card.
 */
export async function fetchReviewerProfile() {
  const { data: authData, error: authError } = await supabase.auth.getUser();
  if (authError) throw authError;
  if (!authData?.user) return null;

  const { data, error } = await supabase
    .from('profiles')
    .select('id, full_name, avatar_url, email, role')
    .eq('id', authData.user.id)
    .single();

  if (error) throw error;
  return data;
}

/**
 * Insert a new review for a completed job_request.
 *
 * @param {Object} params
 * @param {string} params.jobRequestId  - job_requests.id (uuid)
 * @param {string} params.proId         - pro_profiles.id (uuid)
 * @param {number} params.rating        - 1-5 overall rating
 * @param {Object} params.categoryRatings - { "Quality of work": 5, ... }
 * @param {string} params.text          - review body text
 * @param {Array}  params.photos        - [{ url, name, size }] local blob refs / placeholder paths
 * @param {number} params.tip           - tip amount in KSh
 */
export async function createReview({ jobRequestId, proId, rating, categoryRatings, text, photos, tip }) {
  const { data: authData, error: authError } = await supabase.auth.getUser();
  if (authError) throw authError;
  if (!authData?.user) throw new Error('You must be signed in to submit a review.');

  // Photos are currently local blob URLs (not yet uploaded to Supabase Storage).
  // Store filenames/placeholder paths only — actual storage upload is a follow-up.
  const photoRecords = (photos || []).map(p => ({
    name: p.name || null,
    size: p.size || null,
    // placeholder path; replace with real storage URL once upload is wired
    path: null,
  }));

  const { data, error } = await supabase
    .from('reviews')
    .insert({
      job_request_id: jobRequestId,
      customer_id: authData.user.id,
      pro_id: proId,
      rating,
      category_ratings: categoryRatings || {},
      review_text: text,
      photos: photoRecords,
      tip_amount: tip || 0,
      moderation_status: 'pending',
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}